import { useState, useEffect, useMemo, useCallback } from 'react'
import PricingClient from '../clients/pricing-client'
import LivePricingModel from '../models/pricing/live-pricing-model'
import { BaseTransport } from '../transports'
import LivePricingResponseTransport, { LivePricingParamsTransport } from '../transports/live-pricing-transport'
import { getModel } from '../utils/model-utils'
import { isValidPeriod, isDateInArray } from '../utils/date-utils'
import isEmpty from 'lodash/isEmpty'
import { FormatDateFunction, TranslationFunction } from '../common/types'
import LivePricingFormatter, { FormattedLivePricingDataTransport } from '../models/pricing/live-pricing-formatter'
import isNumber from 'lodash/isNumber'
import { PriceFormatter } from '../hooks/price'
import { usePropertyDetailsContext } from './property-details'
import { useSessionEffect } from './session'

interface UseLivePricingState {
    pricingDetails: FormattedLivePricingDataTransport | null
    isLoading: boolean
    error: number | null
    responseData: BaseTransport<LivePricingResponseTransport> | null
}

enum LPS_ERROR_ENUM {
    GENERAL_ERROR = 0,
    INVALID_ARRIVAL_DAY = 61,
    INVALID_DEPARTURE_DAY = 62,
    TOO_MANY_PERSONS = 43,
    INVALID_ARRIVAL_DEPARTURE_DATE = 48,
}

const createLivePricingModel = (response: BaseTransport<LivePricingResponseTransport>): LivePricingModel => {
    if (!response?.data) {
        throw new Error('No pricing data available.')
    }
    return getModel<LivePricingResponseTransport, LivePricingModel>(response.data, response.meta)
}

const isValidAvailabilityData = (params: {
    availableArrivalDates: Date[] | null
    availableDepartureDates: Date[] | null
}): boolean => {
    return !isEmpty(params.availableArrivalDates) && !isEmpty(params.availableDepartureDates)
}

const validatePricingParams = (
    params: LivePricingParamsTransport,
    availableArrivalDates: Date[] | null,
    availableDepartureDates: Date[] | null,
    maxAllowedPerson: number,
): { isValid: boolean; error: number | null } => {
    const arrivalDate = params.arrival
    const departureDate = params.departure
    const numberOfChildren = params.childrenAges === '' ? 0 : params.childrenAges.split(',').length
    const totalPersons = params.adults + numberOfChildren

    if (totalPersons > maxAllowedPerson) {
        return { isValid: false, error: LPS_ERROR_ENUM.TOO_MANY_PERSONS }
    }
    if (!arrivalDate || !departureDate) {
        return { isValid: false, error: null }
    }
    if (!isValidPeriod(arrivalDate, departureDate)) {
        return { isValid: false, error: LPS_ERROR_ENUM.INVALID_ARRIVAL_DEPARTURE_DATE }
    }
    if (!isValidAvailabilityData({ availableArrivalDates, availableDepartureDates })) {
        return { isValid: false, error: LPS_ERROR_ENUM.INVALID_ARRIVAL_DEPARTURE_DATE }
    }
    if (!isDateInArray(arrivalDate, availableArrivalDates)) {
        return { isValid: false, error: LPS_ERROR_ENUM.INVALID_ARRIVAL_DAY }
    }
    if (!isDateInArray(departureDate, availableDepartureDates)) {
        return { isValid: false, error: LPS_ERROR_ENUM.INVALID_DEPARTURE_DAY }
    }

    return { isValid: true, error: null }
}

export const useLivePricing = (
    params: LivePricingParamsTransport,
    availableArrivalDates: Date[] | null,
    availableDepartureDates: Date[] | null,
    maxAllowedPerson = 12,
    formatDate: FormatDateFunction,
    t: TranslationFunction,
    formatPrice: PriceFormatter,
    isAvailabilityLoading: boolean,
) => {
    const [state, setState] = useState<UseLivePricingState>({
        pricingDetails: null,
        isLoading: false,
        error: null,
        responseData: null,
    })

    const { isComboListing } = usePropertyDetailsContext()
    const livePricingFormatter = useMemo(
        () => new LivePricingFormatter(formatDate, t, formatPrice),
        [formatDate, t, formatPrice],
    )

    const fetchLivePricing = useCallback(
        async (
            params: LivePricingParamsTransport,
            availableArrivalDates: Date[] | null,
            availableDepartureDates: Date[] | null,
            maxAllowedPerson: number,
        ) => {
            const { isValid, error } = validatePricingParams(
                params,
                availableArrivalDates,
                availableDepartureDates,
                maxAllowedPerson,
            )

            if (!isValid) {
                setState({
                    pricingDetails: null,
                    isLoading: false,
                    error,
                    responseData: null,
                })
                return
            }

            setState(prevState => ({ ...prevState, isLoading: true, error: null }))

            try {
                const response = await PricingClient.getLivePrices({ params })

                setState({
                    pricingDetails: null, // Will be set by memoized value
                    isLoading: false,
                    error: null,
                    responseData: response,
                })
            } catch (error: any) {
                setState({
                    pricingDetails: null,
                    isLoading: false,
                    error: isNumber(error.message) ? error.message : LPS_ERROR_ENUM.GENERAL_ERROR,
                    responseData: null,
                })
            }
        },
        [],
    )

    // Memoized live pricing model based on response data
    const livePricingModel = useMemo(() => {
        if (!state.responseData) {
            return null
        }
        try {
            return createLivePricingModel(state.responseData)
        } catch {
            return null
        }
    }, [state.responseData])

    // Memoized formatted pricing model based on live pricing model
    const formattedPricingModel = useMemo(() => {
        if (!livePricingModel || !livePricingFormatter) {
            return null
        }
        try {
            return livePricingFormatter.getAllFormattedData(livePricingModel)
        } catch {
            return null
        }
    }, [livePricingModel, livePricingFormatter])

    // Update pricingDetails when formattedPricingModel changes
    useEffect(() => {
        if (formattedPricingModel && !state.isLoading && !state.error) {
            setState(prevState => ({
                ...prevState,
                pricingDetails: formattedPricingModel,
            }))
        }
    }, [formattedPricingModel, state.isLoading, state.error])

    useSessionEffect(() => {
        if (!isComboListing && !isAvailabilityLoading) {
            fetchLivePricing(params, availableArrivalDates, availableDepartureDates, maxAllowedPerson)
        }
    }, [
        availableArrivalDates,
        availableDepartureDates,
        fetchLivePricing,
        isAvailabilityLoading,
        isComboListing,
        maxAllowedPerson,
        params,
    ])

    return {
        ...state,
        livePricingModel,
        formattedPricingModel,
    }
}
